import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { BrevoList, useAddBrevoSubscriber, useBrevoListSubscribers } from '../../queries/brevoLists';

interface BrevoListSubscribersDialogProps {
  list: BrevoList;
  onClose: () => void;
}

// "Remove subscriber" isn't here - the old admin's own button for this is a
// client-side alert('not implemented yet') stub with no backing route, so
// there's nothing to port.
export function BrevoListSubscribersDialog({ list, onClose }: BrevoListSubscribersDialogProps) {
  const { data, isLoading } = useBrevoListSubscribers(list.id);
  const addSubscriber = useAddBrevoSubscriber(list.id);

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    setError(null);
    addSubscriber.mutate(
      { email },
      {
        onSuccess: () => setEmail(''),
        onError: (err) => setError(extractErrorMessage(err, 'Failed to add subscriber.')),
      },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">Subscribers — {list.name}</Dialog.Title>

          <div className="mt-3 flex items-center gap-2">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={addSubscriber.isPending || !email.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {addSubscriber.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Add
            </button>
          </div>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="mt-4 max-h-80 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Name</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(data?.subscribers ?? []).map((subscriber) => (
                    <tr key={subscriber.email}>
                      <td className="px-3 py-2">{subscriber.email}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {[subscriber.firstName, subscriber.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                    </tr>
                  ))}
                  {(data?.subscribers.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={2} className="px-3 py-6 text-center text-muted-foreground">
                        No subscribers yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Close
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
